export const integrationEndpoints = [
  ['GET', '/revisions', 'Revisions newest first, immutable definition and instanceSchema.'],
  ['POST', '/images/uploads', 'Create a resumable ZIP upload for revisionId.'],
  ['POST', '/images/:imageId/upload-parts', 'Sign the requested partNumbers for direct object-storage PUT.'],
  ['GET', '/images/:imageId/upload', 'Recover uploaded parts, bytes and validation state.'],
  ['POST', '/images/:imageId/complete-upload', 'Complete multipart upload and validate the ZIP.'],
  ['GET', '/images/:imageId', 'Image status and Agent replicas.'],
  ['GET', '/images/:imageId/events', 'SSE upload, validation and build progress.'],
  ['GET', '/build-agents', 'VPCs currently eligible for custom image builds.'],
  ['POST', '/images/:imageId/builds', 'Build on the selected agentId.'],
  ['POST', '/instances', 'Prepare an instance using the pinned revision and ready replica.'],
  ['GET', '/instances/:instanceId', 'Instance state, preparation, lifecycle and endpoints.'],
  ['POST', '/instances/:instanceId/start', 'Start a prepared or stopped instance.'],
  ['POST', '/instances/:instanceId/stop', 'Stop the instance.'],
  ['POST', '/instances/:instanceId/restart', 'Restart the instance.'],
] as const;

// The generated example contains placeholders and public product identity only.
// API keys are read from the caller's environment, never embedded by the UI.
export function multipartUploadExample(baseUrl: string): string {
  return `// Save as upload.mjs; run with Node.js 20+:
// node upload.mjs ./build.zip VERSION REVISION_ID
// Set HOSTINATOR_KEY in your process/CI environment first.
// Keep VERSION and REVISION_ID unchanged when resuming the same upload.
import { createReadStream } from 'node:fs';
import { stat } from 'node:fs/promises';
import { createHash } from 'node:crypto';

const base = ${JSON.stringify(baseUrl)};
const key = process.env.HOSTINATOR_KEY;
const [file, version, revisionId] = process.argv.slice(2);
if (!key || !file || !version || !revisionId) {
  throw new Error('Set HOSTINATOR_KEY; provide ZIP path, version and pinned revisionId.');
}

async function api(path, body, idempotencyKey) {
  const response = await fetch(base + path, {
    method: body === undefined ? 'GET' : 'POST',
    redirect: 'error',
    headers: {
      Authorization: 'Bearer ' + key,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(JSON.stringify(result));
  return result;
}

const sizeBytes = (await stat(file)).size;
const hash = createHash('sha256');
for await (const chunk of createReadStream(file)) hash.update(chunk);
const sha256 = hash.digest('hex');
const payload = { revisionId, version, sha256, sizeBytes };
// Deterministic for this exact upload: retry never creates another release.
const uploadKey = 'upload-' + createHash('sha256')
  .update(base + JSON.stringify(payload)).digest('hex');
const { image } = await api('/images/uploads', payload, uploadKey);
console.log('imageId:', image.id);
const status = await api('/images/' + image.id + '/upload');

if (status.image.artifact.status !== 'uploading') {
  console.log('Artifact state:', status.image.artifact.status);
  if (['failed', 'expired', 'deleting'].includes(status.image.artifact.status)) {
    throw new Error('This upload cannot continue. Inspect its status before creating a new release.');
  }
  if (status.image.artifact.status === 'initiating') {
    console.log('Initialization is pending. Wait, then rerun this exact command to resume.');
  }
  console.log('Use the image events/status endpoints to follow validation or build.');
} else {
  if (!status.upload) throw new Error('No active upload session. Inspect artifact state.');
  const uploaded = new Set(status.parts.map(part => part.partNumber));
  let uploadedBytes = status.parts.reduce((sum, part) => sum + part.sizeBytes, 0);
  for (let partNumber = 1; partNumber <= status.upload.totalParts; partNumber++) {
    if (uploaded.has(partNumber)) continue;
    const { urls } = await api('/images/' + image.id + '/upload-parts', {
      partNumbers: [partNumber],
    });
    const part = urls[0];
    const start = (partNumber - 1) * status.upload.partSizeBytes;
    // Send only ZIP bytes to object storage. NEVER add Authorization here.
    const body = createReadStream(file, { start, end: start + part.sizeBytes - 1 });
    try {
      const response = await fetch(part.url, {
        method: 'PUT', redirect: 'error', duplex: 'half',
        headers: { 'Content-Length': String(part.sizeBytes) }, body,
      });
      if (!response.ok) throw new Error('Part ' + partNumber + ': HTTP ' + response.status);
      await response.arrayBuffer();
    } finally {
      body.destroy();
    }
    uploadedBytes += part.sizeBytes;
    console.log('Uploaded bytes:', uploadedBytes, '/', sizeBytes,
      Math.round(uploadedBytes / sizeBytes * 100) + '%');
  }
  await api('/images/' + image.id + '/complete-upload', {}, 'complete-' + image.id);
  console.log('Upload complete; wait for artifact.status = uploaded before building.');
}`;
}
